"use client"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { exportToCSV, buildExportData, printReportWindow } from "@/lib/export-utils"
import type { ExportOptions } from "@/lib/export-utils"
import { ExportModal } from "@/components/export-modal"
import { PortfolioTabs } from "@/components/portfolio-tabs"
import type { ProcessedDLOMResults } from "@/lib/api-service"

export function DiscountMatrixTable({
  data,
  title,
  description,
}: {
  data: number[][]
  title: string
  description: string
}) {
  if (!data || !Array.isArray(data) || data.length !== 3) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">No data available</p>
        </CardContent>
      </Card>
    )
  }

  const rowLabels = [
    "Short Liquidation Period (25th Percentile)",
    "Median Liquidation Period",
    "Long Liquidation Period (75th Percentile)",
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          {title}
        </CardTitle>
        <p className="text-sm text-muted-foreground">{description}</p>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{title}</TableHead>
              <TableHead className="text-right">Low Volatility (25th Percentile)</TableHead>
              <TableHead className="text-right">Median Volatility</TableHead>
              <TableHead className="text-right">High Volatility (75th Percentile)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row, rowIndex) => (
              <TableRow key={rowIndex} className={rowIndex % 2 === 0 ? "bg-muted/30" : undefined}>
                <TableCell className="font-medium">{rowLabels[rowIndex]}</TableCell>
                <TableCell className="text-right">{(row[0] * 100).toFixed(3)}%</TableCell>
                <TableCell className="text-right">{(row[1] * 100).toFixed(3)}%</TableCell>
                <TableCell className="text-right">{(row[2] * 100).toFixed(3)}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

interface ResultsPanelProps {
  results?: ProcessedDLOMResults
}

export function ResultsPanel({ results }: ResultsPanelProps) {
  const hasResults = !!results

  const handleExportCSV = async (options: ExportOptions) => {
    if (!results) return
    try {
      const exportData = buildExportData(results, options)
      exportToCSV(exportData)
    } catch (error) {
      console.error("[v0] CSV export error:", error)
    }
  }

  const handleExportPDF = async (options: ExportOptions) => {
    if (!results) return
    try {
      printReportWindow(results, options)
    } catch (error) {
      console.error("[v0] PDF export error:", error)
    }
  }

  // old in-page print flow removed

  if (!hasResults) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Calculation Results</h1>
            <p className="text-muted-foreground mt-2">Configure parameters and calculate to view discount analysis</p>
          </div>
          <Badge variant="secondary" className="px-3 py-1.5">
            Ready
          </Badge>
        </div>


        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="shadow-sm">
            <CardHeader className="pb-4 border-b">
              <CardTitle className="text-lg font-semibold">Statistical Analysis</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="text-center py-6">
                <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">📊</span>
                </div>
                <p className="text-muted-foreground text-sm">
                  Volatility and half-life statistics will be displayed here
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader className="pb-4 border-b">
              <CardTitle className="text-lg font-semibold">Portfolio Analysis</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="text-center py-6">
                <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">📈</span>
                </div>
                <p className="text-muted-foreground text-sm">
                  Detailed portfolio matrices and analysis will appear here
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">Calculation Results</h2>
        <div className="flex space-x-2">
          <ExportModal onExportCSV={handleExportCSV} onExportPDF={handleExportPDF} />
        </div>
      </div>


      <PortfolioTabs portfolios={results.portfolios} />
      {/* no in-page print container; we open a new window for print */}
    </div>
  )
}
